/* global globalThis */
/**
 * Vérification d'un registre de preuve exporté (EVIDENCE-LEDGER.md), sans
 * MonAlerte : mêmes formules que le serveur. Cette fonction est aussi
 * recopiée telle quelle dans verify.html (dossier de preuve hors ligne) :
 * elle ne doit dépendre de rien d'autre que WebCrypto.
 */
export async function verifyLedgerExport(registre) {
    const enc = new TextEncoder();
    const sha256Hex = async text => Array.from(new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', enc.encode(text))),
        b => b.toString(16).padStart(2, '0')).join('');
    const canonical = value => {
        if (value === null || typeof value !== 'object') return JSON.stringify(value === undefined ? null : value);
        if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
        return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + canonical(value[key])).join(',') + '}';
    };
    const iso = value => (value ? new Date(value).toISOString() : null);
    const report = { chain: { ok: true, length: 0, headHash: null, error: null }, anchors: [] };
    let prev = '0'.repeat(64);
    const entries = registre.entries || [];
    for (let i = 0; i < entries.length; i += 1) {
        const e = entries[i];
        if (Number(e.seq) !== i + 1) { report.chain = { ok: false, length: i, headHash: prev, error: 'numérotation interrompue à ' + e.seq }; break; }
        if (e.prevHash !== prev) { report.chain = { ok: false, length: i, headHash: prev, error: 'chaînage rompu à ' + e.seq }; break; }
        const hash = await sha256Hex(canonical(['monalerte-ledger-v1', String(registre.activityId), Number(e.seq), prev, e.entryType,
            e.payloadSha256 || null, e.objectKey || null, e.actorUserId === undefined ? null : e.actorUserId, e.deviceId || null,
            iso(e.clientTime), iso(e.gnssTime), iso(e.receivedAt), e.details || {}]));
        if (hash !== e.entryHash) { report.chain = { ok: false, length: i, headHash: prev, error: 'entrée modifiée : ' + e.seq }; break; }
        prev = hash;
        report.chain = { ok: true, length: i + 1, headHash: prev, error: null };
    }
    const bySeq = new Map(entries.map(e => [Number(e.seq), e.entryHash]));
    // Racine de l'arbre propre à la session : une feuille par entrée (seq 1..n),
    // les paires de gauche à droite, un nœud seul remonte tel quel.
    const sessionRoot = async upToSeq => {
        let level = [];
        for (const e of entries) {
            if (Number(e.seq) > upToSeq) break;
            level.push(await sha256Hex('leaf:' + registre.activityId + ':' + Number(e.seq) + ':' + e.entryHash));
        }
        if (!level.length) return null;
        while (level.length > 1) {
            const next = [];
            for (let i = 0; i < level.length; i += 2) {
                next.push(level[i + 1] === undefined ? level[i] : await sha256Hex('node:' + level[i] + level[i + 1]));
            }
            level = next;
        }
        return level[0];
    };
    for (const anchor of registre.anchors || []) {
        let node;
        if ((anchor.merkleProof || []).length === 0 && anchor.anchorReason !== 'AUDIT') {
            // Horodatage par session : tout l'arbre est recalculé depuis les entrées.
            node = await sessionRoot(Number(anchor.upToSeq));
        } else {
            // Anciens ancrages groupés : chemin de la tête de chaîne jusqu'à la racine.
            node = await sha256Hex('leaf:' + registre.activityId + ':' + anchor.upToSeq + ':' + anchor.headHash);
            for (const step of anchor.merkleProof || []) {
                node = await sha256Hex('node:' + (step.side === 'L' ? step.hash + node : node + step.hash));
            }
        }
        report.anchors.push({
            upToSeq: anchor.upToSeq,
            headMatchesChain: bySeq.get(Number(anchor.upToSeq)) === anchor.headHash,
            merkleOk: node === anchor.merkleRoot,
            merkleRoot: anchor.merkleRoot,
            hasTimestamp: Boolean(anchor.tsaResponse),
            reason: anchor.anchorReason || null,
            qualified: Boolean(anchor.qualified),
            tsaProvider: anchor.tsaProvider || null,
            createdAt: anchor.createdAt || null,
        });
    }
    return report;
}
